import select from 'dom-select';

const format = (cents, format = '${{amount}}') => {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  let value = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format;

  function formatWithDelimiters(
    number,
    precision = 2,
    thousands = ',',
    decimal = '.',
  ) {
    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${thousands}`,
    );
    const centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_space_separator':
      value = formatWithDelimiters(cents, 2, ' ', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = formatWithDelimiters(cents, 0, ' ', ',');
      break;
  }

  return formatString.replace(placeholderRegex, value);
};

/**
 * A very simple helper function that checks
 * a single breakpoint. Note: this is a way to guess
 * at the device type, it is by no means the best way.
 */
export const isMobile = () => window.innerWidth < 768;

export const isTablet = () => window.innerWidth < 1023;

/**
 * A helper function that checks for the current breakpoint.
 * Note that these values need to be kept in sync with the CSS breakpoints.
 */
export const getBreakpoint = () => {
  if (window.innerWidth < 415) {
    return 'xxs';
  } else if (window.innerWidth > 414 && window.innerWidth < 768) {
    return 'xs';
  } else if (window.innerWidth > 769 && window.innerWidth < 1024) {
    return 's';
  } else if (window.innerWidth > 1023 && window.innerWidth < 1201) {
    return 'm';
  } else if (window.innerWidth > 1200 && window.innerWidth < 1441) {
    return 'md';
  } else if (window.innerWidth > 1440) {
    return 'l';
  }
};

/**
 * Checks to see if the device supports touch gestures.
 */
export const isTouch = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints;
};

/**
 * Checks to see if the device supports object-fit styles.
 * This is useful to understand if you need to leverage a
 * shim over object-fit styles.
 */
export const supportsObjectFit = () => {
  let objectFit = false;
  for (const prop in document.documentElement.style) {
    if (/object(?:-f|F)it$/.test(prop)) {
      objectFit = true;
      break;
    }
  }
  return objectFit;
};

/**
 * A very simple check to see if the device is an IE device.
 */
export const isIE = () => {
  return navigator.userAgent.toLowerCase().indexOf('msie') > 0;
};

/**
 * A very simple check to see if the device is an IE or Edge device.
 */
export const isIEorEdge = () => {
  if (document.documentMode || /Edge/.test(navigator.userAgent)) {
    return true;
  } else {
    return false;
  }
};

/**
 * A very simple check to see if the device is a Firefox device.
 */
export const isFirefox = () => {
  return navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
};

/**
 * A helper function that updates the innerHTML
 * of a DOM element.
 *
 * @param {Node} el The element to modify
 * @param {String} value The value to inject
 */
export const write = (el, value) => {
  el.innerHTML = value;
};

/**
 * A helper function to add a class
 * to a DOM element, or an array of DOM elements.
 *
 * @param {Node|Array} item A single DOM element or an Array
 * @param {String} selector  The class to add
 */
export const set = (item, selector) => {
  if (item instanceof Array) {
    for (const i of item) {
      i.classList.add(selector);
    }
  } else {
    item.classList.add(selector);
  }
};

/**
 * A helper function to remove a class
 * to a DOM element, or an array of DOM elements.
 *
 * @param {Node|Array} item A single DOM element or an Array
 * @param {String} selector  The class to remove
 */
export const unset = (item, selector) => {
  if (item instanceof Array) {
    for (const i of item) {
      i.classList.remove(selector);
    }
  } else {
    item.classList.remove(selector);
  }
};

/**
 * A helper function  that checks to
 * see if a DOM element has a class attached
 * to it.
 *
 * @param {Node} item A single DOM element
 * @param {String} selector  The class to check
 */
export const contains = (item, selector) => {
  return item.classList.contains(selector);
};

/**
 * A helper function to toggle the class
 * of a DOM element, or an array of DOM elements.
 *
 * @param {Node|Array} item A single DOM element or an Array
 * @param {String} selector  The class to toggle
 */
export const toggle = (item, selector) => {
  if (item instanceof Array) {
    for (const i of item) {
      i.classList.toggle(selector);
    }
  } else {
    item.classList.toggle(selector);
  }
};

/**
 * Get the height of a DOM element
 *
 * @param {Node} el
 */
export const getHeight = (el) => {
  return `${el.offsetHeight}px`;
};

/**
 * Get the width of a DOM element
 *
 * @param {Node} el
 */
export const getWidth = (el) => {
  return `${el.offsetWidth}px`;
};

/**
 * Checks to see if the current viewport width
 * is over a certain integer.
 *
 * @param {Integer} breakpoint
 */
export const isOver = (breakpoint) => {
  return window.innerWidth > breakpoint;
};

/**
 * Clones a complex Javascript object. This is a
 * thorough way to ensure that the object is fully
 * cloned with no references kept in place.
 *
 * @param {Object} obj
 */
export const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Return moneySeparator from __GLOBAL__.moneyFormat
 */
export const moneySeparator = () => {
  if (window.__GLOBAL__.moneyFormat.includes('_with_comma_separator')) {
    return ',';
  }
  return '.';
};

/**
 * Formats a given integer to a standard price.
 *
 * @param {Integer} num A price, with cents
 * @param {Boolean} trailing
 * @param {Boolean} currency
 */
export const formatPrice = (num, trailing = false, currency = false) => {
  let moneyString = currency
    ? format(num, window.__GLOBAL__.moneyFormatWithCurrency)
    : format(num, window.__GLOBAL__.moneyFormat);
  if (trailing) {
    moneyString = moneyString.replace(`${moneySeparator()}00`, '');
  }
  return moneyString;
};

/**
 * Converts price to a number if the price is a string
 * but we need it as a number.
 *
 * @param {DOM Node} element
 */
export const formatPriceString = (data) => {
  let price;
  if (typeof data === 'string') {
    price = Number(data.replace(/[^0-9.-]+/g, ''));
  } else {
    price = data;
  }
  return price;
};

/**
 * Removes whitespace within a string and makes all
 * characters lowercase.
 *
 * @param {String} str
 */
export const handleize = (str) => {
  if (!str) return;
  return str
    .trim()
    .replace(/[ /_]/g, '-')
    .replace('&amp;', '')
    .replace('#39;', '')
    .replace(/[&,+:,']/g, '')
    .replace(/-+/g, '-')
    .replace(new RegExp(/[àáâãäå]/g), 'a')
    .replace(new RegExp(/æ/g), 'ae')
    .replace(new RegExp(/ç/g), 'c')
    .replace(new RegExp(/[èéêë]/g), 'e')
    .replace(new RegExp(/[ìíîï]/g), 'i')
    .replace(new RegExp(/ñ/g), 'n')
    .replace(new RegExp(/[òóôõö]/g), 'o')
    .replace(new RegExp(/œ/g), 'oe')
    .replace(new RegExp(/[ùúûü]/g), 'u')
    .replace(new RegExp(/[ýÿ]/g), 'y')
    .toLowerCase();
};

/**
 * Decodes a string that has been encoded through the 'url_encode' Shopify filter
 * @param {*} str
 */
export const decode = (str) => decodeURIComponent(str.replace(/\+/g, ' '));

/**
 * Decodes HTML entities
 * @param {*} str
 */
export const htmlDecode = (input) => {
  const doc = new DOMParser().parseFromString(input, 'text/html');
  return doc.documentElement.textContent;
};

/**
 * Generates a URL that is suitable for the Shopify
 * image CDN. Adds the current image size suffix.
 *
 * @param {*} src
 * @param {*} size
 */
export const getImageWithSize = (src = '', size = 1000) => {
  return ('' + src).replace(/([.](?:jpe?g|png))/, `_${size}x$1`);
};

/**
 * Used by components like the product card to select
 * the current product image based on the active color.
 * If there is no active color, a fallback image
 * should be returned if it is defined.
 *
 * @param {*} name
 * @param {*} value
 * @param {*} images
 * @param {*} featured
 * @param {*} fallback
 */
export const getProductImage = (
  name = '',
  value = '',
  images,
  featured = false,
  fallback = false,
  gwpImage = false,
) => {
  const key = `${name}-${(value || '').replace(/[/ ]/g, '-')}`.toLowerCase();
  const image = (images || []).find(({ src }) => {
    return ~src.indexOf(key) && ~src.indexOf(featured ? 'pos-1' : 'pos-2');
  });
  if (gwpImage) {
    return getImageWithSize(gwpImage, 600);
  }
  if (!image) {
    return getImageWithSize(fallback, 600);
  }
  return getImageWithSize(image, featured ? 1200 : 600);
};

/**
 * Decode Storefront Product ID, this is used
 * with Storefront API
 *
 * @param {*} src
 * @param {*} size
 */
export const decodeIDproduct = (id) => {
  return decodeURIComponent(id).split('gid://shopify/Product/')[1];
};

/**
 * Decode Storefront Product ID, this is used
 * with Storefront API
 *
 * @param {*} src
 * @param {*} size
 */
export const decodeIDvariant = (id) => {
  return decodeURIComponent(id).split('gid://shopify/ProductVariant/')[1];
};

/**
 * Encode from numerical to Storefront Product ID, this is used
 * with Storefront API
 *
 * @param {*} id
 */
export const encodeIDproduct = (id) => {
  return 'gid://shopify/Product/' + id;
};

/**
 * Decode Storefront Product Variant ID, this is used
 * with Storefront API
 *
 * @param {*} src
 * @param {*} size
 */
export const decodeIDProductVariant = (id) => {
  return decodeURIComponent(id).split('gid://shopify/ProductVariant/')[1];
};

/**
 * Builds an srcset string
 *
 * @param {String} url The master image URL
 */
export const generateSrcset = (url) => {
  if (!url) return false;

  const mobileURL = getImageWithSize(url, 768);
  const tabletURL = getImageWithSize(url, 1024);
  const desktopURL = getImageWithSize(url, 1440);

  return `${mobileURL} 768w, ${tabletURL} 1024w, ${desktopURL} 1440w`;
};

/**
 * In order to add and remove listeners on demand we need this 2 variables:
 */
let bodyCloseListeners = [];

export const addCloseListener = (callback) => {
  const body = document.querySelector('body');
  // For modals and ajax cart the listener it's the same, one it's enough
  if (bodyCloseListeners.length >= 1) {
    return;
  }
  // Adding this function in order to remove it later
  const customCallback = (event) => {
    // Only works when pressing esc
    if (event.which === 27 || event.keyCode === 27) {
      // Removing focus on any input
      window.focus();
      if (document.activeElement) {
        document.activeElement.blur();
      }
      // Excecuting callback
      callback(event);
    }
  };

  bodyCloseListeners.push({
    element: body,
    type: 'keyup',
    callback: customCallback,
  });

  body.addEventListener('keyup', customCallback);
};

export const clearAllCloseListeners = () => {
  bodyCloseListeners.forEach((item) => {
    try {
      item.element.removeEventListener(item.type, item.callback, false);
    } catch (error) {
      console.warn(error);
    }
  });
  bodyCloseListeners = [];
};

/**
 * Focus first input when form it's displayed
 *
 * @param {*} id Form Id to focus
 * @param {*} delay If form is inside one model delay is necesary to focus first input
 */
export const focusFirstInputOfForm = (id = false, delay = 0) => {
  if (id) {
    setTimeout(() => {
      document.querySelector(`#${id} .text-input__wrapper input`).focus();
    }, delay); // Delay
  }
};

/**
 * Generates a slug from a readable value
 *
 * @param {*} value Readable value
 */
export const slug = (value) => value.toLowerCase().replace(' ', '-');

/**
 * Adds and removes the ability to gain focus using the
 * attribute tabindex="0" and tabindex="-1".
 * When an element has a positive tabindex value, it gets focus.
 * @param {*} element Element that you would like to toggle
 * focus ability
 */

export const toggleFocusAbility = (element) => {
  if (
    element.tabIndex === -1 ||
    (!element.tabIndex &&
      (!element.tagName === 'A' ||
        !element.tagName === 'BUTTON' ||
        !element.tagName === 'INPUT' ||
        !element.tagName === 'TEXTAREA' ||
        !element.tagName === 'SELECT' ||
        !element.tagName === 'DETAILS'))
  ) {
    element.tabIndex = 0;
  } else if (
    element.tabIndex === 0 ||
    (element.tabIndex !== -1 &&
      (element.tagName === 'A' ||
        element.tagName === 'BUTTON' ||
        element.tagName === 'INPUT' ||
        element.tagName === 'TEXTAREA' ||
        element.tagName === 'SELECT' ||
        element.tagName === 'DETAILS'))
  ) {
    element.tabIndex = -1;
  } else {
    return true;
  }
};

/**
 * Selects all focusable elements within an element
 * @param {*} element Element that you would like to search in, for
 * focusable elements
 */
export const getAllFocusableEl = (el) =>
  select.all(
    'a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])',
    el,
  ) || [];

/**
 * Gets all focusable elements and changes their tabindex to 0 if set to -1 or
 * chanes their tabindex to -1 if they are set to 0 or have no tabindex set
 */

export const changeTabIndexFocusEl = (el) => {
  getAllFocusableEl(el).forEach((el) => {
    toggleFocusAbility(el);
    return true;
  });
};

/**
 * Returns true if Tab is pressed
 */
export const isTabPressed = (e) => {
  return e.key === 'Tab' || e.keyCode === 9;
};

/**
 * Converts snake or kebab case to camel case
 * @param {String} string String to convert
 */
export const toCamel = (string) =>
  string.replace(/([-_][a-z])/g, (group) =>
    group.toUpperCase().replace('-', '').replace('_', ''),
  );

/**
 * Accurately determines if something is an object
 * @param {*} obj Variable to check
 */
export const isObject = function (obj) {
  return (
    obj === Object(obj) && !Array.isArray(obj) && typeof obj !== 'function'
  );
};

/**
 * Converts all keys (including nested) in an object to camel case
 * @param {Object} obj Object to convert
 */
export const keysToCamel = function (obj) {
  if (isObject(obj)) {
    const n = {};

    Object.keys(obj).forEach((k) => {
      n[toCamel(k)] = keysToCamel(obj[k]);
    });

    return n;
  } else if (Array.isArray(obj)) {
    return obj.map((i) => {
      return keysToCamel(i);
    });
  }

  return obj;
};

/**
 * Ensures product JSON matches expected format
 * @param {Object} product Product to cleanse
 */
export const cleanseProductJson = (product) => {
  let result = {};
  /* Product JSON response may be missing expected properties or be
   * formatted in camel case */
  result = keysToCamel(product);

  result.id = parseInt(product.id);

  if (!result.optionsWithValues) {
    result.optionsWithValues = result.options.reduce((obj, option) => {
      obj[option.name] = option.values;
      return obj;
    }, {});
  }

  if (!result.optionNames) {
    result.optionNames = result.options.map((option) => option.name);
  }

  if (!result.shadeCount) {
    result.shadeCount = false;
  }

  // If images are in flattened format, convert to object for compatibility
  if (
    result.images &&
    result.images.length &&
    !Object.keys(result.images[0]).includes('src')
  ) {
    result.images = result.images.map((image) => ({ src: image }));
  }

  return result;
};

/**
 * Decode Storefront Product ID, this is used
 * with Storefront API
 *
 * @param {*} src
 * @param {*} size
 */
export const decodeObjectID = (id, object = 'Product') => {
  return decodeURIComponent(id).split(`gid://shopify/${object}/`)[1];
};

/**
 * Encode Storefront Product ID, this is used
 * with Storefront API
 *
 * @param {*} src
 * @param {*} size
 */
export const encodeObjectID = (id, object = 'Product') => {
  return `gid://shopify/${object}/${id}`;
};

/**
 * Encode product properties
 *
 * @param {*} properties Product properties
 */
export const encodeProperties = (properties) => {
  if (properties['_item_list_name']) {
    properties['_item_list_name'] = encodeURIComponent(
      properties['_item_list_name'],
    );
  }

  if (properties['_origin_product']) {
    properties['_origin_product'] = encodeURIComponent(
      properties['_origin_product'],
    );
  }

  if (properties['_product_collection']) {
    properties['_product_collection'] = encodeURIComponent(
      properties['_product_collection'],
    );
  }
  if (properties['_promo_text'] && properties['_promo_text'] !== '') {
    properties['_promo_text'] = encodeURIComponent(properties['_promo_text']);
  }

  if (properties['_product_tags']) {
    properties['_product_tags'] = properties['_product_tags'].map((tag) =>
      encodeURIComponent(tag),
    );
  }

  if (properties['_bundle_product_title']) {
    properties['_bundle_product_title'] = encodeURIComponent(properties['_bundle_product_title']);
  }

  return properties;
};

/**
 * Takes a GraphQL Storefront representation
 * of a product and converts it into a format
 * that can hydrate the product-card component
 *
 * @param {*} p The GraphQL Product
 */
export const convertGQLToProductJson = (p) => {
  if (!p) {
    return false;
  }

  const media = p?.media?.edges.map((e) => {
    let item;
    if (e.node.mediaContentType === 'EXTERNAL_VIDEO') {
      item = {
        type: 'video',
        url: e.node.embedUrl,
        host: e.node?.host,
        previewImage: {
          src: e.node.previewImage?.src,
          alt: e.node.previewImage?.altText,
        },
      };
    } else {
      item = {
        type: 'image',
        src: e.node.previewImage.src,
        alt: e.node.previewImage.altText,
      };
    }
    return item;
  });

  const options = p?.options || [];
  const metafields = {
    general: {},
    banner: {},
    product_bundles: {},
    accentuate: {},
    swatch_management: {},
    yotpo: {},
    cart_promo: {},
    card: {},
  };
  const mfs = p.metafields?.filter((m) => m) || [];
  if (p && mfs.length) {
    for (const [key, value] of Object.entries(metafields)) {
      mfs.forEach((element) => {
        if (key === element.namespace) {
          if (key === 'accentuate') {
            if (element.key === 'bundled_products') {
              metafields[element.namespace][element.key] = JSON.parse(
                element.value,
              );
            }
          } else {
            metafields[element.namespace][element.key] = element.value;
          }
        }
      });
    }
  }

  let fallbackImage = null;
  if (media[0]?.src) {
    fallbackImage = {
      src: media[0].src,
      alt: p.title,
    };
  }

  const collections = p?.collections?.edges.map((edge) => edge.node.title);
  const awardsData = p.metafields.find(
    (metafield) => metafield?.key === 'award_images',
  );

  const result = {
    id: decodeObjectID(p.id),
    title: p.title,
    description: p.description,
    categories: collections,
    handle: p.handle,
    tags: p.tags,
    type: p.productType,
    available: p.availableForSale,
    awards: awardsData ? JSON.parse(awardsData.value) : null,
    media: media,
    featuredImage: media[0]?.src || '',
    variants: p.variants.edges.map((e) => {
      return {
        title: e.node.title,
        id: decodeObjectID(e.node.id, 'ProductVariant'),
        sku: e.node.sku,
        compareAtPrice: e.node.compareAtPrice?.amount * 100 || null,
        price: e.node.price.amount * 100,
        image: {
          src: e.node.image?.src || fallbackImage?.src,
          alt: e.node.image?.altText || fallbackImage?.alt,
        },
        available: e.node.availableForSale,
        quantityAvailable: e.node.quantityAvailable,
        sizeSubtitle: e.node.sizeSubtitle?.value,
        shortDescription: e.node.shortDescription?.value,
        sourceGWPCollectionItem: e.node.sourceGWPCollectionItem?.value,
        bundleBuilderProductIds: e.node.bundleBuilderProductIds?.value,
        isGWPVariant: e.node.isGWPVariant?.value,
        isBundleVariant: e.node.isBundleVariant?.value,
        sellingPlanAllocations: e.node?.sellingPlanAllocations?.edges.map(
          (s) => {
            return {
              id: decodeObjectID(s.node.sellingPlan.id, 'SellingPlan'),
              name: s.node.sellingPlan.name,
              options: s.node.sellingPlan.options,
            };
          },
        ),
      };
    }),
    options,
    optionNames: options.map((o) => o.name),
    price: p.priceRange.minVariantPrice.amount * 100,
    compareAtPrice: p.compareAtPriceRange.minVariantPrice.amount * 100,
    colors: false,
    badge: false,
    metafields: metafields,
    vendor: p.vendor,
    isBundle: Object.hasOwn(metafields.accentuate, 'bundled_products'),
    shortDescription: metafields.general?.['short_description'],
    cardDescription: metafields.card?.['description'],
  };
  const optionsWithValues = [];
  options.forEach((o) => {
    result[o.name] = o.values;
    optionsWithValues[o.name] = o.values;
  });
  result.optionsWithValues = optionsWithValues;
  return result;
};

/**
 * Converts a single level object to a query string to consumed by nanoajax
 */
export const serializeToQs = (body) => {
  return Object.entries(body)
    .filter(([_, val]) => typeof val !== 'undefined')
    .map(([key, val]) => `${key}=${encodeURIComponent(val)}`)
    .join('&');
};

/*
 * Get Product Category from Product Tags
 *
 * @param {*} tags
 */
export const getCategoryFromTags = (tags = []) => {
  const res = tags.find((tag) => tag.includes('storefront:category:'));
  return res ? res.split('storefront:category:')[1] : false;
};

/*
 * This returned string will have the size removed
 * from the variant SKU for Segment.
 *
 * @param {*} String A Variant SKU
 */
export const getMasterSku = (string = '') => {
  const elements = string.split('_');
  elements.pop();
  return elements.join('_');
};

/**
 * Adds a protocol to protocal-relative url strings.
 *
 * @param {DOM Node} element
 */
export const urlCheckProtocol = (string = '') => {
  let url;
  const protocol = 'https:';
  if (string.includes('http:') || string.includes('https:')) {
    url = string;
  } else if (string.includes('//')) {
    url = protocol.concat('', string);
  } else {
    url = string;
  }
  return url;
};

/**
 * Generate HTML stars for Yopto
 *
 * @param {*} number
 */
export const getStarsHTML = (number = -1) => {
  if (number < 0) {
    number = 0;
  }
  const average = Math.round(parseFloat(number) * 2);
  const imax = 10;
  let outputHTML = '';
  let i = 2;
  while (i <= imax) {
    if (i <= average) {
      outputHTML +=
        '<span class="yotpo-icon review-sta yotpo-icon-star" aria-hidden="true" aria-label="icon full star" aria-labelledby="icon-full-star" ></span>';
    } else {
      if (average + 1 === i) {
        outputHTML +=
          '<span class="yotpo-icon review-sta yotpo-icon-half-star" aria-hidden="true" aria-label="icon half star" aria-labelledby="icon-half-star" ></span>';
      } else {
        outputHTML +=
          '<span class="yotpo-icon review-sta yotpo-icon-empty-star" aria-hidden="true" aria-label="icon empty star" aria-labelledby="icon-empty-star" ></span>';
      }
    }
    i += 2;
  }
  return `<div class="yotpo">
      <span class="sr-only">${number} star rating</span>
      <span class="stars-wrapper">
        ${outputHTML}
      </span>
    </div>`;
};

export const getStarsLabel = (product) => {
  const reviewsData = product.reviewsAverage
    ? product.reviewsAverage
    : product.metafields?.yotpo?.reviews_average;

  const reviewsCountData = product.reviewsCount
    ? `(${product.reviewsCount})`
    : `(${product.metafields?.yotpo?.reviews_count})`;
  if (!reviewsData) return '';

  return `${reviewsData} out of 5 stars based on ${reviewsCountData} reviews`;
};

/**
 * Sort array by key
 *
 * @param {Array} array
 * @param {String} key
 * @param {String} order
 */
export const sortArray = (array = [], key = 'id', order = 'asc') => {
  const dir = order === 'asc' ? 1 : -1;
  return array.sort((a, b) => {
    const x = a[key];
    const y = b[key];
    return x < y ? -1 * dir : x > y ? 1 * dir : 0;
  });
};

/**
 * Converts and object to a URL params string (w/nested elements)
 *
 * @param {Object} obj
 */
export const objectToUrlParams = (obj, separator = '[]') => {
  function iter(obj, path) {
    if (Array.isArray(obj)) {
      obj.forEach(function (temp) {
        if (separator === '[]') {
          iter(temp, path + '[]');
        } else {
          iter(temp, path + '.');
        }
      });
      return;
    }
    if (obj !== null && typeof obj === 'object') {
      Object.keys(obj).forEach(function (key) {
        if (separator === '[]') {
          iter(obj[key], path + '[' + key + ']');
        } else {
          iter(obj[key], path + '.' + key);
        }
      });
      return;
    }
    data.push(path + '=' + encodeURIComponent(obj));
  }

  const data = [];
  Object.keys(obj).forEach(function (key) {
    iter(obj[key], key);
  });
  return data.join('&');
};

/**
 * A function that sends a request to an endpoint
 * recommended to chain actions by using `.then/.catch`
 */
export const ajaxRequest = (endpoint = '', data = {}, method = 'get') => {
  method = method.toLowerCase();
  const ajaxData = {
    headers: { Accept: 'application/json' },
    withCredentials: true,
    cors: true,
    method: method,
  };
  if (data && Object.keys(data).length > 0) {
    let body = '';
    if (method === 'post') {
      body = JSON.stringify(data);
      ajaxData.body = body;
    } else {
      body = objectToUrlParams(data);
      ajaxData.url += '?' + body;
    }
  }

  return new Promise((resolve, reject) => {
    fetch(endpoint, ajaxData)
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (!data || data.error) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((e) => {
        reject(e);
      });
  });
};

/**
 * Capitalize first letter (with javascript)
 *
 * @param {String} string
 */
export const capitalize = (string = '') => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

/**
 * Returns a new document title, to be used when the
 * product changes dynamically with Javascript, without
 * a full page reload.
 *
 * @param {Array} tags
 * @param {String} oldTitle
 */
export const generateNewDocumentTitleOnShadeChange = (shade, previousShade) => {
  const toReplace = new RegExp(previousShade, 'i');
  return document.title.replace(toReplace, capitalize(shade));
};

/*
 * Parses JSON, removing any comments that
 * Shopify might throw at us.
 *
 * @param {*} string
 */
export const parseJSON = (string) => {
  let json = {};
  string = string.replace(/<!--.*-->/g, '');
  try {
    json = JSON.parse(string);
  } catch (e) {
    console.log(e);
  }
  return json;
};

/**
 * Takes a GraphQL Storefront representation
 * of a collection an select only products
 * with images and convert them them into
 * product-card data structure
 *
 * @param {*} array The GraphQL response
 */
export const cleanStorefrontResponse = (array) => {
  return array
    .filter((p) => p.node.images.edges.length > 0)
    .map((p) => convertGQLToProductJson(p.node));
};

/**
 * Traps focus in modal
 */

const modalListeners = [];

export const trapFocus = (element) => {
  const selectors = [
    '[tabindex]:not([tabindex="-1"])',
    'a[href]:not([disabled])',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input[type="text"]:not([disabled])',
    'input[type="radio"]:not([disabled])',
    'input[type="checkbox"]:not([disabled])',
    'select:not([disabled])',
  ].join(', ');

  const focusableEls = select.all(selectors, element);
  const firstFocusableEl = focusableEls[0];
  const lastFocusableEl = focusableEls[focusableEls.length - 1];
  if (firstFocusableEl !== lastFocusableEl) {
    firstFocusableEl.focus();

    const trapFocusActions = (e) => {
      var isTabPressed = e.key === 'Tab' || e.keyCode === 9;
      if (!isTabPressed) {
        return;
      }

      if (e.shiftKey) {
        /* shift + tab */ if (document.activeElement === firstFocusableEl) {
          lastFocusableEl.focus();
          e.preventDefault();
        }
      } /* tab */ else {
        if (document.activeElement === lastFocusableEl) {
          firstFocusableEl.focus();
          e.preventDefault();
        }
      }
    };

    modalListeners.push({
      element: element,
      type: 'keydown',
      callback: trapFocusActions,
    });

    element.addEventListener('keydown', trapFocusActions);
  }
};

export const swatchDescription = ({
  shade,
  swatch_color_id: swatchColorId,
}) => {
  const shadeObject = __GLOBAL__.shades.values.find(
    (s) => s.id.toLowerCase() === swatchColorId.toLowerCase(),
  );

  let description = '';
  if (shadeObject) {
    description = shadeObject.description;
  }
  return `${shade} - ${description}`;
};

/* Generates markup for a custom dropdown containing swatches */
export const swatchOptionMarkup = ({
  available,
  swatch_color_id: swatchColorId,
  shade,
  hexOverride,
  imageOverride,
}) => {
  const shadeObject = __GLOBAL__.shades.values.find(
    (s) => s.id.toLowerCase() === swatchColorId.toLowerCase(),
  );
  let description = '';
  if (shadeObject) {
    description = shadeObject.description;
  }

  let swatchClasses = `fenty-color-${swatchColorId}`;
  if (!available) {
    swatchClasses += ' unavailable';
  }

  return `
    <div class="dropdown-swatch f aic">
      <span
        class="dropdown-swatch__swatch ${swatchClasses}"
        style="
          ${hexOverride ? `background-color: ${hexOverride} !important;` : ''}
          ${
            imageOverride
              ? `
            background-color: unset !important;
            background-image: url('${imageOverride}');
          `
              : ''
          }
        "
      >
        <span class="cross"></span>
      </span>
      <div class="dropdown-swatch__info f fdc align-l">
        <span class="dropdown-swatch__label grey-dark">
          <span class="dropdown-swatch__label--shade">${shade}</span><span class="dropdown-swatch__label--description"> - ${description}</span>
        </span>
      </div>
    </div>
  `;
};

/**
 * Generate markup for custom dropdown with shade category/range swatch
 * @param {String} hande swatch handle
 * @param {String} title - human readable swatch title
 */
export const categorySwatchOptionMarkup = (handle, title) => {
  return `
    <div class="category-dropdown-label">
      <span class="category-dropdown-label__swatch fenty-range-color-${handle}"></span>
      <span class="category-dropdown-label__title">${title}</span>
    </div>
  `;
};
/**
 * Gets size option from variant object
 */
export const getVariantSize = (variant) => {
  if (variant?.Size?.[0]) {
    return variant.Size[0];
  }
  if (variant.optionsWithValues?.Size?.[0]) {
    return variant.optionsWithValues.Size[0];
  }
  return null;
};

const SIZE_DELIMITER = '||';
/*
 * Removes size from variant title
 */
export const removeSizeFromTitle = (title) => {
  return title.split(SIZE_DELIMITER)[0].trim();
};

/**
 * Finds closest parent elemnt that matches the selector
 * @param {DOMElement} elem dom element.
 * @param {String} selector - css selector
 */
export function getClosest(elem, selector) {
  let elemTemp = elem;

  for (; elemTemp && elemTemp !== document; elemTemp = elemTemp.parentNode) {
    if (elemTemp.matches(selector)) {
      return elemTemp;
    }
  }
  return null;
}

/**
 * Get all siblings of an element.
 * (c) 2018 Chris Ferdinandi, MIT License, https://gomakethings.com.
 * @param {Node} element - The element you want get the siblings of.
 * @returns {Array}
 */
export function getSiblings(element) {
  return Array.prototype.filter.call(element.parentNode.children, (sibling) => {
    return sibling !== element;
  });
}

/**
 * Converts data from an Accentuate namespace into a more usable format
 * @param {Array} data - data from Accentuate
 */
export const normalizeAccentuateData = (data) => {
  const content = [];
  const dataDecoded = JSON.parse(decode(data));

  const firstProp = dataDecoded[Object.keys(dataDecoded)[0]];

  if (!firstProp) return [];

  // Computes number of blocks from length of array in first property, and populates with empty objects
  dataDecoded[Object.keys(dataDecoded)[0]].forEach(() => {
    content.push({});
  });

  for (const key in dataDecoded) {
    dataDecoded[key].forEach((value, index) => {
      content[index][toCamel(key)] =
        key === 'position' ? parseInt(value) : value;
    });
  }

  return content;
};

/**
 * Helper function for inserting elements after  targetElement
 */
export function insertAfter(newElement, targetElement) {
  // target is what you want it to go after. Look for this elements parent.
  const parent = targetElement.parentNode;

  // if the parents lastchild is the targetElement...
  if (parent.lastChild === targetElement) {
    // add the newElement after the target element.
    parent.appendChild(newElement);
  } else {
    // else the target has siblings, insert the new element between the target and it's next sibling.
    parent.insertBefore(newElement, targetElement.nextSibling);
  }
}

/*
 * Get all value for specific tag
 * @param {String} tagName
 * @param {*} tags
 */
export const getAllValueForTag = (tagName, tags = [], divider = '|') => {
  const res = tags
    ?.filter((tag) => tag.includes(`${tagName}${divider}`))
    .map((tag) => tag.split(`${tagName}${divider}`)[1]);
  return res && res.length > 0 ? res : false;
};

/*
 * Get value for specific tag
 * @param {String} tagName
 * @param {*} tags
 */
export const getValueForTag = (tagName, tags = [], divider = '|') => {
  const res = tags?.find((tag) => tag.includes(`${tagName}${divider}`));
  return res ? res.split(`${tagName}${divider}`)[1] : false;
};

/*
 * Checks if a specific tag exists
 * @param {String} tagName
 * @param {*} tags
 */
export const hasTag = (tagName, tags = []) => {
  const res = tags.find((tag) => tag.includes(tagName));
  return res;
};

/*
 * Checks product limit set in tags for specific countries
 * By default limit is set to 5
 * @param {*} tags
 */
export const getProductLimit = (tags) => {
  let productLimit = 5;
  const restrictedQuantity = tags.reduce((acc, tag) => {
    if (tag.startsWith('shipping-restricted-qty')) {
      const data = tag.split('shipping-restricted-qty|')[1];
      const [country, quantity] = data?.split('|');
      acc.push({ [country]: Number(quantity) });
    }
    return acc;
  }, []);

  if (restrictedQuantity.length) {
    const el = restrictedQuantity.find((item) => item[window.shippingCode]);
    productLimit = el && el[window.shippingCode];
  }

  return productLimit;
};

/**
 * Call/Try with delay, until returnValue.complete === true or it reaches N times
 * @param {*} func function to be passed in
 * * @func should return isComplete? boolean
 * @param {*} limit Number of times to be tried
 * @param {*} delay
 */
export const tryNTimes = (func, limit, delay) => {
  if (!limit) return;
  const val = func();
  if (val === true) {
    return;
  }
  setTimeout(() => {
    tryNTimes(func, limit - 1, delay);
  }, delay);
};

/**
 * @param {DOMElement} el
 * @param {function} function to call
 * @returns Runs your function when the element is visible on the screen
 */
export function intersectionObserver(el, operation) {
  if (!el) return;

  const options = {
    root: null,
    threshold: 0.5,
  };

  const callback = function (entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) operation();
    });
  };

  const observer = new IntersectionObserver(callback, options);
  observer.observe(el);
}

export const checkBundleAvailability = async (product, storefront, queries) => {
  if (
    !product.isBundle ||
    !product.bundleProductsData ||
    product.bundleProductsData.length === 0
  ) {
    return false;
  }

  const bundleRestrictedProducts = product.bundleProductsData.filter(
    (bundleProduct) =>
      (bundleProduct.tags ?? []).some((tag) =>
        tag.includes('shipping-restricted-replacement'),
      ),
  );

  if (bundleRestrictedProducts.length === 0) {
    return false;
  }

  for (const bundleProduct of bundleRestrictedProducts) {
    const restrictedProductTag = bundleProduct.tags?.find((tag) =>
      tag.includes(`shipping-restricted-replacement|${window.shippingCode}`),
    );

    if (restrictedProductTag) {
      const restrictedProductId = restrictedProductTag.split(
        `shipping-restricted-replacement|${window.shippingCode}|`,
      )[1];
      try {
        const response = await storefront.request(
          queries.productAvailabilityByVariantId,
          {
            id: `gid://shopify/ProductVariant/${restrictedProductId}`,
          },
        );
        return !response.node;
      } catch (error) {
        console.error('Error fetching product availability:', error);
        // Handle error accordingly, e.g., return false or throw error
        return false;
      }
    }
  }
  return false;
};

export const checkProductAvailability = async (
  product,
  storefront,
  queries,
) => {
  if (!product) {
    return false;
  }

  const restrictedProductTag = product.tags?.find((tag) =>
    tag.includes(`shipping-restricted-replacement|${window.shippingCode}`),
  );

  if (restrictedProductTag) {
    const restrictedProductId = restrictedProductTag.split(
      `shipping-restricted-replacement|${window.shippingCode}|`,
    )[1];
    try {
      const response = await storefront.request(
        queries.productAvailabilityByVariantId,
        {
          id: `gid://shopify/ProductVariant/${restrictedProductId}`,
        },
      );
      return !response.node;
    } catch (error) {
      console.error('Error fetching product availability:', error);
      // Handle error accordingly, e.g., return false or throw error
      return false;
    }
  }
  return false;
};
/**
 * Inut a phone number and return a number formatted for Attentive
 * @param {String} value Phone number
 * @param {String} countryCode Country code
 * @returns {String} Formatted phone number
 */
export const formatAsPhoneNumber = (value, countryCode = '') => {
  if (!value) return '';

  // Remove non-digit characters
  const cleanedValue = value.replace(/\D/g, '');

  // Check the length to determine if country code is needed
  let formattedValue = '';

  if (countryCode === 'GB' && !cleanedValue.startsWith('44')) {
    // UK number without country code
    if (cleanedValue.length === 10) {
      const x = cleanedValue.match(/(\d{4})(\d{6})/);
      formattedValue = `+44${x[1]}${x[2]}`;
    } else {
      // Format as is if it doesn't match expected length
      formattedValue = `+44${cleanedValue}`;
    }
  } else if (cleanedValue.length === 10) {
    // US/Canada number without country code
    const x = cleanedValue.match(/(\d{3})(\d{3})(\d{4})/);
    formattedValue = `+1${x[1]}${x[2]}${x[3]}`;
  } else if (cleanedValue.length === 11 && cleanedValue.startsWith('1')) {
    // US/Canada number with country code
    const x = cleanedValue.match(/(\d{1})(\d{3})(\d{3})(\d{4})/);
    formattedValue = `+${x[1]}${x[2]}${x[3]}${x[4]}`;
  } else if (cleanedValue.length === 11 && cleanedValue.startsWith('44')) {
    // UK number without country code
    const x = cleanedValue.match(/(\d{2})(\d{4})(\d{6})/);
    formattedValue = `+${x[1]}${x[2]}${x[3]}`;
  } else if (cleanedValue.length === 12 && cleanedValue.startsWith('44')) {
    // UK number with country code
    const x = cleanedValue.match(/(\d{2})(\d{4})(\d{6})/);
    formattedValue = `+${x[1]}${x[2]}${x[3]}`;
  } else {
    // For other cases, just format the digits as is
    const x = cleanedValue.match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
    formattedValue = `${x[1]}${x[2]}${x[3]}`;
  }

  return formattedValue;
};


